import React from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, money } from "@/components/common";
import DataModule from "@/components/DataModule";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, GasPump } from "@phosphor-icons/react";

function expiryBadge(dateStr) {
  if (!dateStr) return <span className="text-sm text-slate-400">—</span>;
  const today = new Date();
  const d = new Date(dateStr);
  const days = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  let cls = "text-emerald-600";
  if (days < 0) cls = "text-red-600 font-semibold";
  else if (days <= 30) cls = "text-amber-600 font-semibold";
  return <span className={`font-mono text-sm ${cls}`}>{dateStr}{days < 0 ? " (expired)" : days <= 30 ? ` (${days}d)` : ""}</span>;
}

export default function Safety() {
  const { user } = useAuth();
  const canWrite = ["admin", "dispatcher"].includes(user.role);

  return (
    <div>
      <PageHeader title="Safety & Compliance" subtitle="Driver documents (CDL, medical) and IFTA fuel records" />
      <Tabs defaultValue="docs">
        <TabsList className="bg-white border border-slate-200 mb-4">
          <TabsTrigger value="docs" data-testid="tab-docs"><ShieldCheck size={16} className="mr-1.5" />Documents</TabsTrigger>
          <TabsTrigger value="ifta" data-testid="tab-ifta"><GasPump size={16} className="mr-1.5" />IFTA</TabsTrigger>
        </TabsList>

        <TabsContent value="docs">
          <DataModule apiPath="safety-docs" queryKey="safety_docs" canWrite={canWrite} icon={ShieldCheck} testid="doc"
            addLabel="Add Document" emptyTitle="No documents tracked" emptyHint="Track CDLs, medical cards and other expiring documents."
            columns={[
              { key: "driver", label: "Driver", render: (r) => <span className="font-medium text-slate-900">{r.driver}</span> },
              { key: "doc_type", label: "Document", render: (r) => <span className="capitalize text-sm text-slate-700">{String(r.doc_type || "").replace(/_/g, " ")}</span> },
              { key: "number", label: "Number", mono: true, hideSm: true },
              { key: "expiry_date", label: "Expires", render: (r) => expiryBadge(r.expiry_date) },
              { key: "status", label: "Status" },
            ]}
            fields={[
              { key: "driver", label: "Driver Name", required: true },
              { key: "doc_type", label: "Document Type", type: "select", options: ["cdl", "medical_card", "drug_test", "mvr", "insurance", "other"], default: "cdl" },
              { key: "number", label: "Document Number" },
              { key: "issue_date", label: "Issue Date", type: "date" },
              { key: "expiry_date", label: "Expiry Date", type: "date" },
              { key: "status", label: "Status", type: "select", options: ["valid", "expired", "pending"], default: "valid" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]} />
        </TabsContent>

        <TabsContent value="ifta">
          <DataModule apiPath="ifta" queryKey="ifta" canWrite={canWrite} icon={GasPump} testid="ifta"
            addLabel="Add IFTA Entry" emptyTitle="No IFTA entries" emptyHint="Log fuel and mileage by state for quarterly IFTA reporting."
            columns={[
              { key: "truck", label: "Truck", mono: true },
              { key: "state", label: "State" },
              { key: "quarter", label: "Quarter", hideSm: true },
              { key: "miles", label: "Miles", render: (r) => <span className="font-mono text-sm text-slate-700">{Number(r.miles || 0).toLocaleString()}</span> },
              { key: "gallons", label: "Gallons", hideSm: true, render: (r) => <span className="font-mono text-sm text-slate-700">{r.gallons}</span> },
              { key: "fuel_cost", label: "Fuel Cost", render: (r) => <span className="font-mono text-sm text-slate-700">{money(r.fuel_cost)}</span> },
            ]}
            fields={[
              { key: "truck", label: "Truck #", required: true },
              { key: "state", label: "State (e.g. TX)", required: true },
              { key: "quarter", label: "Quarter", type: "select", options: ["Q1", "Q2", "Q3", "Q4"], default: "Q2" },
              { key: "miles", label: "Miles", type: "number" },
              { key: "gallons", label: "Gallons", type: "number" },
              { key: "fuel_cost", label: "Fuel Cost ($)", type: "number" },
            ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
